import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

export interface AutotagProcessingData {
  ignoreTemplates: boolean;
  pagesStatus: WritableSignal<string[]>;
}

interface ProcessingStatus {
  status: string;
  iconTemplate: string;
}

@Component({
  selector: 'lib-autotag-processing',
  imports: [NgTemplateOutlet],
  templateUrl: './autotag-processing.component.html',
  styleUrl: './autotag-processing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutotagProcessingComponent implements OnInit, OnChanges {
  @Input() progress = 0;
  @Input() ignoreTemplates = false;
  @Input() error: string | null = null;

  @Output() exit = new EventEmitter<void>();

  status: ProcessingStatus | undefined = undefined;

  private statuses: ProcessingStatus[] = [
    {
      status: 'Analyzing pages',
      iconTemplate: 'analyzingPagesIcon',
    },
    {
      status: 'Matching templates',
      iconTemplate: 'matchingTemplatesIcon',
    },
    {
      status: 'Applying Optical Character Recognition',
      iconTemplate: 'applyingOCRIcon',
    },
    {
      status: 'Checking tag entities',
      iconTemplate: 'checkingEntitiesIcon',
    },
    {
      status: 'Generating signer tags',
      iconTemplate: 'generatingMarkupIcon',
    },
    {
      status: 'Finalizing AutoTag',
      iconTemplate: 'finalizingAutoTagIcon',
    },
  ];

  ngOnChanges(change: SimpleChanges): void {
    if (change['progress']) {
      const progress = change['progress'].currentValue;
      this.status = this.getStatus(progress);
    }
    if (change['error'] && this.error) {
      this.status = {
        iconTemplate: 'errorIcon',
        status: this.error,
      };
    }
  }

  ngOnInit(): void {
    // Skip 'Matching templates' status if ignoreTemplates is true
    if (this.ignoreTemplates) {
      const matchingTemplateStatus = this.statuses.find(
        (status) => status.status === 'Matching templates',
      ) as ProcessingStatus;
      matchingTemplateStatus.iconTemplate = this.statuses[0].iconTemplate;
      matchingTemplateStatus.status = this.statuses[0].status;
    }
  }

  /**
   * Emits an event to indicate the exit action from the component.
   */
  onExit() {
    this.exit.emit();
  }

  /**
   * Calculates and returns the processing status based on the given progress percentage.
   *
   * @param {number} progress - The current progress of the document processing.
   * @returns {ProcessingStatus} The current processing status.
   */
  private getStatus(progress: number): ProcessingStatus {
    const index = Math.min(
      Math.floor(progress * this.statuses.length),
      this.statuses.length - 1,
    );
    const currentStatus = this.statuses[index];
    return currentStatus || this.statuses[this.statuses.length - 1];
  }
}
